import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShell } from './ShellContext';
import { managerIntelAPI } from '../utils/api';
import { NAV_ITEMS } from './navConfig';

/**
 * ⌘K palette — jump to any page, league, or recently scouted manager.
 */
const CommandPalette = ({ onClose, toast }) => {
  const navigate = useNavigate();
  const { leagues, sleeperUser } = useShell();
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [recentManagers, setRecentManagers] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    let active = true;
    managerIntelAPI
      .getRecentManagers(8)
      .then((res) => {
        if (!active) return;
        const list = res.data?.managers || res.data || [];
        if (Array.isArray(list)) setRecentManagers(list);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const items = useMemo(() => {
    const all = [
      ...NAV_ITEMS.map((item) => ({
        label: item.label,
        typ: 'Page',
        go: () => navigate(item.path),
      })),
      ...leagues.map((league) => ({
        label: league.name,
        typ: 'League',
        go: () => {
          const params = new URLSearchParams();
          if (league.name) params.set('name', league.name);
          if (sleeperUser?.userId) params.set('userId', sleeperUser.userId);
          if (sleeperUser?.username) params.set('username', sleeperUser.username.toLowerCase());
          navigate(`/simulate/${league.leagueId}?${params.toString()}`);
        },
      })),
      ...recentManagers
        .map((m) => m.sleeperUsername || m.username)
        .filter(Boolean)
        .map((username) => ({
          label: username,
          typ: 'Manager',
          go: () => navigate(`/manager-intel/${username}`),
        })),
    ];
    const q = query.trim().toLowerCase();
    return q ? all.filter((i) => i.label.toLowerCase().includes(q)) : all.slice(0, 10);
  }, [query, leagues, recentManagers, navigate, sleeperUser]);

  useEffect(() => {
    if (highlight >= items.length) setHighlight(0);
  }, [items, highlight]);

  const run = (item) => {
    onClose();
    item.go();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && items[highlight]) {
      run(items[highlight]);
    }
  };

  return (
    <div className="ft-palette" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ft-pbox">
        <input
          ref={inputRef}
          value={query}
          placeholder="Jump to a page, league, or manager"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <ul className="ft-plist">
          {items.length === 0 ? (
            <li className="ft-pempty">Nothing matches</li>
          ) : (
            items.map((item, ix) => (
              <li
                key={`${item.typ}-${item.label}`}
                className={ix === highlight ? 'hl' : ''}
                onMouseEnter={() => setHighlight(ix)}
                onClick={() => run(item)}
              >
                {item.label}
                <span className="ft-typ">{item.typ}</span>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
};

export default CommandPalette;
